/**
 * Cloudscape light/dark theme context.
 *
 * Cloudscape ships `applyMode(Mode.Dark | Mode.Light, document.body)` for
 * theming. This module wraps that primitive in a React context so any
 * component can read the current mode and toggle it. The user's choice
 * is persisted to `localStorage` under `agentmesh.theme`; cross-Stage
 * cache flush (`useStageWatcher`) deliberately wipes every key prefixed
 * `agentmesh.` on a hostname change, so flipping between dev and prod
 * also resets the theme to the system default — matches the cross-Stage
 * isolation posture the rest of the SPA already enforces.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { applyMode, Mode } from "@cloudscape-design/global-styles";

const STORAGE_KEY = "agentmesh.theme";

export type ThemeMode = "light" | "dark";

interface ThemeContextValue {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (next: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Read the persisted mode from localStorage, falling back to the
 * `prefers-color-scheme` media query, then to light as a last resort.
 * SSR-safe — when `window` is unavailable, returns `"light"`.
 */
function resolveInitialMode(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* localStorage unavailable (private mode, quota); fall through */
  }
  if (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Override the resolved initial mode in tests. */
  initialMode?: ThemeMode;
}

export function ThemeProvider({ children, initialMode }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(
    initialMode ?? resolveInitialMode(),
  );

  // Apply the Cloudscape body class on every mode change. The library
  // mutates `document.body.classList` so a single global call is enough.
  useEffect(() => {
    applyMode(mode === "dark" ? Mode.Dark : Mode.Light, document.body);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* persistence is best-effort */
    }
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
  const toggle = useCallback(
    () => setModeState((prev) => (prev === "dark" ? "light" : "dark")),
    [],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, toggle, setMode }),
    [mode, toggle, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Hook for consumers of {@link ThemeContext}. Throws outside the provider. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within <ThemeProvider>");
  }
  return ctx;
}
